import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-uikit/Typography";
import { Button as CompassButton } from "@circleco/compass/components/Button";
import { IconButton } from "@circleco/compass/components/IconButton";

import { t } from "@/i18n-js/instance";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import type { ViewProfileButtonProps } from "./types";

const sizeClasses = {
  sm: "!h-7 !px-3 gap-1.5",
  md: "!h-8 !px-4 gap-2",
  lg: "!h-10 !px-5 gap-2.5",
  full: "w-full",
};

const iconSizes = {
  sm: 14,
  md: 16,
  lg: 18,
  full: 16,
};

export const ViewProfileButton = ({
  member,
  fullWidth,
  onProfileOpen,
  variant = "default",
  size = "md",
  className,
  disabled,
}: ViewProfileButtonProps) => {
  const { isCircleV4 } = useCircleV4();
  const { show: showMemberProfile } = useProfileDrawer();

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    void showMemberProfile({ memberPublicId: member.public_uid ?? "" });
    onProfileOpen?.();
  };

  const isIconOnly = variant === "icon-only";

  if (isCircleV4) {
    if (isIconOnly) {
      return (
        <IconButton
          variant="ghost"
          icon="people"
          disabled={disabled}
          onClick={handleClick}
          aria-label={t("view_profile")}
          data-testid="view-profile-button"
        />
      );
    }
    return (
      <CompassButton
        variant="outline"
        startIcon="people"
        disabled={disabled}
        onClick={handleClick}
        aria-label={t("view_profile")}
        data-testid="view-profile-button"
        fullWidth={fullWidth}
        className={className}
      >
        {t("view_profile")}
      </CompassButton>
    );
  }

  const iconSize = iconSizes[size];

  return (
    <Button
      variant="secondary"
      onClick={handleClick}
      aria-label={t("view_profile")}
      data-testid="view-profile-button"
      full={!isIconOnly && fullWidth}
      className={classNames(
        "!border-primary !font-medium flex justify-center items-center !gap-1",
        sizeClasses[size],
        isIconOnly ? "!w-8 !px-0 min-w-0" : "",
        className,
      )}
      disabled={disabled}
      large={size === "lg"}
    >
      <Icon type="16-people" size={iconSize} useWithFillCurrentColor />
      {!isIconOnly && (
        <Typography.LabelSm color="text-primary" weight="font-medium">
          {t("view_profile")}
        </Typography.LabelSm>
      )}
    </Button>
  );
};
